import { Brain, Search, Sparkles } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { buildScoringTiers } from "../utils";
import { SearchIcon, XIcon } from "./Icons";

// Tier thresholds come from the recommended-threshold sheet (Domains column).
const scoringTiers = buildScoringTiers("DOMAINS", [
	{
		key: "bestMatch",
		label: "Best Match",
		meaning: "The most accurate results.",
		warning: false,
	},
	{
		key: "strongMatch",
		label: "Strong Match",
		meaning: "Very relevant results.",
		warning: false,
	},
	{
		key: "acceptable",
		label: "Acceptable",
		meaning: "Results are generally relevant.",
		warning: false,
	},
	{
		key: "extended",
		label: "Extended",
		meaning: "Includes less relevant results.",
		warning: true,
	},
	{
		key: "maximumReach",
		label: "Maximum Reach",
		meaning: "Shows all qualifying results; many may have low relevance.",
		warning: true,
	},
]);

export const SearchInterface = ({
	// Props from useSearchInterface hook
	searchQuery,
	searchStage,
	refinedQuery,
	isSearching,
	performanceMetric: hookPerformanceMetric, // ✅ RENAMED to avoid conflict
	minReachThreshold: hookMinReachThreshold, // ✅ RENAMED to avoid conflict

	// Event handlers from hook
	onSearchInputChange,
	onRefinedQueryChange,
	onSearch,
	onClearRefinement,
	onPerformanceMetricChange,
	onMinReachChange,

	// Additional props
	activeTab,
	filters = {},
	className = "",

	// NEW: Props for search terms persistence
	searchTerms = [],
	onSearchTermsChange,
	initialSortBy = "compositeScore",
	initialSortOrder = "desc",
	onSortChange,
}) => {
	// ✅ REMOVED: searchTerms state - now using props
	const [performanceMetric, setPerformanceMetric] = useState(
		hookPerformanceMetric || "viewability",
	);
	const [performanceGoalType, setPerformanceGoalType] =
		useState("market_request");
	const [minValue, setMinValue] = useState("700"); // ✅ ALWAYS start with 100
	const [isLLMMode, setIsLLMMode] = useState(false);
	const [searchType, setSearchType] = useState("domain");

	// ✅ NEW: Replace currentScore with selectedTier
	const [selectedTier, setSelectedTier] = useState(scoringTiers[1]); // Default to "Strong Match"
	// ✅ NEW: Sort functionality
	const [sortBy, setSortBy] = useState(initialSortBy);
	const [sortOrder, setSortOrder] = useState(initialSortOrder);

	const performanceMetricOptions = [
		{ value: "viewability", label: "Viewability" },
		{ value: "ctr", label: "CTR (Click-Through Rate)" },
		{ value: "vtr", label: "VTR (View-Through Rate)" },
		{ value: "reach", label: "Market Requests" },
		{ value: "impressions", label: "Impressions" },
	];

	// ✅ NEW: Sort options
	const sortOptions = [
		{ value: "totalImpressions", label: "Impressions" },
		{ value: "domainReach", label: "Reach" },
		{ value: "avgViewability", label: "Viewability" },
		{ value: "calculated_ctr", label: "CTR" },
		{ value: "domainName", label: "Domain Name" },
		{ value: "totalCprMarketRequests", label: "Market Request" },
		{ value: "compositeScore", label: "Composite Score" },
	];

	const TierDropdown = ({ selectedTier, onTierChange, scoringTiers }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		const handleTierSelect = (e) => {
			const tierLabel = e.target.value;
			const tier = scoringTiers.find((t) => t.label === tierLabel);
			onTierChange(tier);
		};

		return (
			<div className="tier-dropdown-container">
				<div className="tier-dropdown-wrapper">
					<select
						value={selectedTier.label}
						onChange={handleTierSelect}
						className="tier-simple-select"
					>
						{scoringTiers.map((tier) => (
							<option key={tier.label} value={tier.label}>
								{tier.label}
							</option>
						))}
					</select>

					<div
						className="info-icon-container"
						onMouseEnter={() => setShowTooltip(true)}
						onMouseLeave={() => setShowTooltip(false)}
					>
						<span className="info-icon">i</span>

						{showTooltip && (
							<div className="tier-tooltip">
								<div className="tooltip-header">
									<strong>Scoring Tiers</strong>
								</div>
								<div className="tooltip-content">
									{scoringTiers.map((tier, index) => (
										<div
											key={index}
											className={`tooltip-tier ${tier.warning ? "warning" : ""}`}
										>
											<div className="tooltip-tier-header">
												<span className="tooltip-tier-label">{tier.label}</span>
												<span className="tooltip-tier-score">
													{tier.score}+
												</span>
											</div>
											<div className="tooltip-tier-meaning">
												{tier.warning && (
													<span className="warning-indicator">⚠️ </span>
												)}
												{tier.meaning}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				<style jsx>{`
        .tier-dropdown-container {
          position: relative;
          width: 100%;
          min-width: 180px;
        }

        .tier-dropdown-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tier-simple-select {
          flex: 1;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #374151;
          font-weight: 600;
        }

        .tier-simple-select:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .tier-simple-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .info-icon-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: help;
        }

        .info-icon {
          font-size: 16px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .info-icon-container:hover .info-icon {
          opacity: 1;
        }

        .tier-tooltip {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 12px;
          z-index: 1000;
          min-width: 320px;
          max-width: 400px;
        }

        .tooltip-header {
          text-align: center;
          font-size: 14px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tooltip-tier {
          padding: 8px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .tooltip-tier.warning {
          background: #fefce8;
          border-color: #fde047;
        }

        .tooltip-tier-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .tooltip-tier-label {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        .tooltip-tier-score {
          font-size: 12px;
          font-weight: 700;
          color: #059669;
          background: #dcfce7;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-score {
          color: #d97706;
          background: #fef3c7;
        }

        .tooltip-tier-meaning {
          font-size: 11px;
          color: #6b7280;
          line-height: 1.4;
          display: flex;
          align-items: flex-start;
          gap: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-meaning {
          color: #92400e;
        }

        .warning-indicator {
          font-size: 10px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .tier-tooltip::after {
          content: '';
          position: absolute;
          bottom: 100%;
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid white;
        }

        .tier-tooltip::before {
          content: '';
          position: absolute;
          bottom: calc(100% + 1px);
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #d1d5db;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .tier-tooltip {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            right: auto;
            bottom: auto;
            width: calc(100vw - 32px);
            max-width: 350px;
            max-height: 60vh;
            overflow-y: auto;
          }

          .tier-tooltip::before,
          .tier-tooltip::after {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .tier-dropdown-container {
            min-width: auto;
          }

          .tier-simple-select {
            font-size: 12px;
            padding: 10px;
          }

          .tier-tooltip {
            width: calc(100vw - 20px);
            max-height: 50vh;
            min-width: auto;
          }

          .tooltip-tier-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .tooltip-tier-score {
            align-self: flex-start;
          }
        }
      `}</style>
			</div>
		);
	};

	useEffect(() => {
		if (filters.searchTerms && Array.isArray(filters.searchTerms)) {
			console.log(
				"🔄 Loading search terms from LLM response:",
				filters.searchTerms,
			);
			// ✅ UPDATED: Use parent state handler
			if (onSearchTermsChange) {
				onSearchTermsChange(filters.searchTerms);
			}
		}
	}, [filters.searchTerms]);

	// ✅ FIXED: Sync with hook state using renamed props
	useEffect(() => {
		if (hookPerformanceMetric && hookPerformanceMetric !== performanceMetric) {
			setPerformanceMetric(hookPerformanceMetric);
		}
	}, [hookPerformanceMetric, performanceMetric]);

	useEffect(() => {
		console.log("📊 SearchInterface: Syncing sort from props:", {
			initialSortBy,
			initialSortOrder,
		});
		setSortBy(initialSortBy);
		setSortOrder(initialSortOrder);
	}, [initialSortBy, initialSortOrder]);

	// ✅ UPDATED: Use selectedTier.score instead of currentScore and update parent state
	const handleAddTermDirect = (termType) => {
		if (searchQuery && searchQuery.trim()) {
			const newTerm = {
				id: Date.now(),
				text: searchQuery.trim(),
				score: selectedTier.score, // display relevance %
				threshold: selectedTier.threshold, // exact cosine distance for the API
				type: termType,
			};

			const updatedTerms = [...searchTerms, newTerm];

			// ✅ Update parent state for persistence
			if (onSearchTermsChange) {
				onSearchTermsChange(updatedTerms);
			}

			// Clear input by calling hook's handler
			if (onSearchInputChange) {
				onSearchInputChange({ target: { value: "" } });
			}
		}
	};

	// ✅ SIMPLIFIED: Remove all suggestion logic
	const handleSearchInputChange = (e) => {
		// Call the original handler from the hook
		if (onSearchInputChange) {
			onSearchInputChange(e);
		}
	};

	const handleSearchKeyPress = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAddTermDirect("include");
		}
	};

	// ✅ UPDATED: Update parent state when removing terms
	const removeTerm = (termId) => {
		const updatedTerms = searchTerms.filter((term) => term.id !== termId);
		if (onSearchTermsChange) {
			onSearchTermsChange(updatedTerms);
		}
	};

	const handlePerformanceMetricChange = (e) => {
		const newMetric = e.target.value;
		setPerformanceMetric(newMetric);

		if (onPerformanceMetricChange) {
			onPerformanceMetricChange(newMetric);
		}
	};

	const handlePerformanceGoalTypeChange = (type) => {
		setPerformanceGoalType(type);
	};

	// ✅ UPDATED: Remove min/max validation
	const handleMinValueChange = (e) => {
		const value = e.target.value;
		setMinValue(value);

		if (onMinReachChange) {
			onMinReachChange(value);
		}
	};

	// ✅ NEW: Simplified tier selection handler
	const handleTierChange = (tier) => {
		setSelectedTier(tier);
	};

	// ✅ NEW: Sort handlers
	const handleSortByChange = (e) => {
		const newSortBy = e.target.value;
		console.log("🔧 Domain Search Interface: Sort by changed to:", newSortBy);
		setSortBy(newSortBy);

		// Notify parent component
		if (onSortChange) {
			onSortChange(newSortBy, sortOrder);
		}
	};

	const handleSortOrderToggle = () => {
		const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
		console.log(
			"🔧 Domain Search Interface: Sort order changed to:",
			newSortOrder,
		);
		setSortOrder(newSortOrder);

		// Notify parent component
		if (onSortChange) {
			onSortChange(sortBy, newSortOrder);
		}
	};

	const handleSearchClick = () => {
		const enhancedFilters = {
			...filters, // Keep all existing filters

			// ✅ CRITICAL: Map React state to API service expected names
			thresholdType: performanceGoalType, // 'reach' or 'market_request'
			minReachDay: parseFloat(minValue) || 100, // Convert string to number, default to 100

			// ✅ ALTERNATIVE NAMES: Support multiple property names
			type: performanceGoalType,
			reachThreshold: parseFloat(minValue) || 100,

			// ✅ NEW: Add sort parameters - MAKE SURE THESE ARE INCLUDED
			sortBy: sortBy,
			sortOrder: sortOrder,
			view: sortOrder, // Also include as 'view' for API compatibility
		};

		const searchOptions = {
			performanceMetric: performanceMetric,
			minValue: minValue,
			sortBy: sortBy,
			sortOrder: sortOrder,
			overrideFilterSort: true,
			filters: enhancedFilters, // ✅ CRITICAL: Pass enhanced filters
		};

		console.log("🚀 Executing search with sort parameters:", {
			sortBy: sortBy,
			sortOrder: sortOrder,
			enhancedFilters: enhancedFilters,
		});

		if (onSearch) {
			onSearch(searchTerms, searchOptions);
		}
	};

	// ✅ UPDATED: Update parent state when clearing all terms
	const handleClearAll = () => {
		if (onSearchTermsChange) {
			onSearchTermsChange([]);
		}
		if (onSearchInputChange) {
			onSearchInputChange({ target: { value: "" } });
		}
	};

	// ✅ SIMPLIFIED: Remove suggestion logic
	const handleSearchInputFocus = () => {
		// Just focus, no suggestions
	};

	return (
		<div className={`search-interface-container ${className}`}>
			{/* Search Terms Display Box */}
			{searchTerms.length > 0 && (
				<div className="search-terms-display">
					<div className="search-terms-content">
						<div className="current-search-term">
							<span>Current search: </span>
							{searchTerms.map((term, index) => (
								<span key={term.id}>
									<span
										className={`search-term ${term.type === "include" ? "term-include" : "term-exclude"}`}
									>
										{term.type === "include" ? "+" : "-"}
										{term.text} ({Math.round(term.score)})
										<button
											onClick={() => removeTerm(term.id)}
											className="term-remove-btn"
										>
											×
										</button>
									</span>
									{index < searchTerms.length - 1 && <span> </span>}
								</span>
							))}
						</div>
					</div>
					<button className="btn-clear-all-inline" onClick={handleClearAll}>
						Clear All
					</button>
				</div>
			)}

			{/* Single Search Row */}
			<div className="search-input-row">
				<div className="search-input-wrapper">
					<SearchIcon />
					<input
						type="text"
						placeholder="try searching for famous football websites in Europe.."
						value={searchQuery || ""} // ✅ FIXED: Use searchQuery from hook
						onChange={handleSearchInputChange}
						onKeyPress={handleSearchKeyPress}
						onFocus={handleSearchInputFocus}
						className="search-input"
					/>

					<div className="term-type-buttons">
						<button
							type="button"
							onClick={() => handleAddTermDirect("include")}
							className="term-type-btn include-btn"
							disabled={!searchQuery || !searchQuery.trim()} // ✅ FIXED: Use searchQuery from hook
						>
							Include
						</button>
						<button
							type="button"
							onClick={() => handleAddTermDirect("exclude")}
							className="term-type-btn exclude-btn"
							disabled={!searchQuery || !searchQuery.trim()} // ✅ FIXED: Use searchQuery from hook
						>
							Exclude
						</button>
					</div>
				</div>

				<div className="tier-input-container">
					<TierDropdown
						selectedTier={selectedTier}
						onTierChange={handleTierChange}
						scoringTiers={scoringTiers}
					/>
				</div>

				<div className="search-button-container">
					<button
						type="button"
						onClick={handleSearchClick}
						className="search-button"
						disabled={isSearching}
					>
						{isSearching ? (
							<>
								<div className="loading-spinner-small"></div>
								Searching...
							</>
						) : (
							"Search"
						)}
					</button>
				</div>
			</div>

			{/* Performance Filter Section */}
			<div className="performance-filter-section">
				<div className="performance-filter-row">
					<div className="performance-filter-container compact">
						<div className="performance-label-container">
							<label className="performance-filter-label">
								Performance KPI
							</label>
						</div>
						<select
							value={performanceMetric}
							onChange={handlePerformanceMetricChange}
							className="performance-filter-select compact"
						>
							{performanceMetricOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>

					{/* ✅ UPDATED: Target with Label at Top */}
					<div className="target-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">
								Target (in Billions)
							</label>
						</div>
						<div className="target-input-group">
							<select
								value={performanceGoalType}
								onChange={(e) =>
									handlePerformanceGoalTypeChange(e.target.value)
								}
								className="target-type-select"
							>
								<option value="reach">Avg. Reach/day</option>
								<option value="market_request">Market Request</option>
							</select>
							<input
								type="number"
								value={minValue}
								onChange={handleMinValueChange}
								className="target-value-input-inline"
								placeholder="100"
							/>
						</div>
					</div>

					{/* ✅ UPDATED: Sort By Section with Arrow */}
					<div className="sort-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">Sort by</label>
						</div>
						<div className="sort-controls">
							<select
								value={sortBy}
								onChange={handleSortByChange}
								className="sort-select"
							>
								{sortOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
							<span
								onClick={handleSortOrderToggle}
								className="sort-order-arrow"
								title={sortOrder === "desc" ? "Descending" : "Ascending"}
							>
								{sortOrder === "desc" ? "↓" : "↑"}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Enhanced Quick Filters Display */}
			<div className="quick-filters">
				{/* Media Subtype */}
				{filters.mediaSubtype && filters.mediaSubtype.length > 0 && (
					<span className="quick-filter">
						Format: {filters.mediaSubtype.join(", ")}
					</span>
				)}

				{/* Device Type */}
				{filters.deviceTypes && filters.deviceTypes.length > 0 && (
					<span className="quick-filter">
						Device: {filters.deviceTypes.join(", ")}
					</span>
				)}

				{/* Video Placement */}
				{filters.videoPlacement && filters.videoPlacement.length > 0 && (
					<span className="quick-filter">
						Placement: {filters.videoPlacement.join(", ")}
					</span>
				)}

				{/* Geography - Selected Countries */}
				{filters.selectedCountries && filters.selectedCountries.length > 0 && (
					<span className="quick-filter">
						Countries:{" "}
						{filters.selectedCountries.length > 3
							? `${filters.selectedCountries.slice(0, 3).join(", ")} +${filters.selectedCountries.length - 3} more`
							: filters.selectedCountries.join(", ")}
					</span>
				)}

				{/* Content Style */}
				{filters.contentStyle && filters.contentStyle.length > 0 && (
					<span className="quick-filter">
						Content: {filters.contentStyle.join(", ")}
					</span>
				)}

				{/* Safety Tier */}
				{filters.safetyTier && filters.safetyTier.length > 0 && (
					<span className="quick-filter">
						Safety: {filters.safetyTier.join(", ")}
					</span>
				)}

				{/* Publishers */}
				{filters.publishers && filters.publishers.length > 0 && (
					<span className="quick-filter">
						Publishers:{" "}
						{filters.publishers.length > 2
							? `${filters.publishers.slice(0, 2).join(", ")} +${filters.publishers.length - 2} more`
							: filters.publishers.join(", ")}
					</span>
				)}

				{/* Is Direct */}
				{filters.isDirect === true && (
					<span className="quick-filter">Direct Only</span>
				)}

				{typeof filters.minViewability === "number" &&
					filters.minViewability !== 0 && (
						<span className="quick-filter">
							Min. Viewability: {filters.minViewability}%
						</span>
					)}

				{typeof filters.minCTR === "number" && filters.minCTR !== 0 && (
					<span className="quick-filter">Min. CTR: {filters.minCTR}%</span>
				)}

				{/* Custom Performance Filters - Show column and threshold from actual structure */}
				{(() => {
					console.log(
						"🔍 Debug - filters.customFilters:",
						filters.customFilters,
					);

					// Check if customFilters exists and has items
					if (!filters.customFilters || filters.customFilters.length === 0) {
						console.log("⚠️ No custom filters found in props");
						return null;
					}

					return filters.customFilters.map((customFilter, index) => {
						console.log("🔍 Debug - Processing custom filter:", customFilter);

						// Simple mapping for common columns (you can expand this as needed)
						const getColumnLabel = (column) => {
							const columnMap = {
								cprmarketrequests: "Market Requests",
								impressions: "Total Impressions",
								viewables: "Viewables",
								viewabilitymeasures: "Viewability Measures",
								completefirstoccurrence: "Video Completions",
								startfirstoccurrence: "Video Starts",
								cprclicks: "CPR Clicks",
								fills: "Fills",
								nonzerobids: "Non Zero Bids",
								dealvolumedealinfo: "Deal Volume Deal Info",
								nonzerobidsvalueinusd: "Non Zero Bids Value (USD)",
								bidspendinusd: "Bid Spend (USD)",
								ctr: "CTR",
								vtr: "VTR",
								revenueperimpression: "Revenue Per Impression",
								costperimpression: "Cost Per Impression",
								bid_guidance: "Bid Guidance",
								bidCpm: "CPM",
								impressionswithclickmacro: "Impressions with Click Macro",
								cprmarketdemandpartnerspendinusd:
									"CPR Market Demand Partner Spend (USD)",
								cprmarketpublisherrevenueinusd:
									"CPR Market Publisher Revenue (USD)",
								cprallrequests: "CPR All Requests",
								cprprivatemarketimpressions: "CPR Private Market Impressions",
							};
							return columnMap[column] || column;
						};

						const columnsToMultiply = [
							"nonzerobidsvalueinusd",
							"bidspendinusd",
							"revenueperimpression",
							"costperimpression",
							"bid_guidance",
							"bidCpm",
						];

						const label = getColumnLabel(customFilter.column);
						const value = columnsToMultiply.includes(customFilter.column)
							? (customFilter.threshold || 0) / 10 ** 6
							: customFilter.threshold || 0;

						return (
							<span
								key={customFilter.id || `custom-${index}`}
								className="quick-filter custom-filter"
							>
								{label} &gt; {value}
							</span>
						);
					});
				})()}

				{/* Selected Categories */}
				{filters.selectedCategories &&
					filters.selectedCategories.length > 0 && (
						<span className="quick-filter">
							Categories:{" "}
							{filters.selectedCategories.length > 2
								? `${filters.selectedCategories.slice(0, 2).join(", ")} +${filters.selectedCategories.length - 2} more`
								: filters.selectedCategories.join(", ")}
						</span>
					)}

				{/* ✅ NEW: Sort Filter */}
				<span className="quick-filter">
					Sort: {sortOptions.find((opt) => opt.value === sortBy)?.label} (
					{sortOrder === "desc" ? "Descending" : "Ascending"})
				</span>

				{/* Distribution Channel (locked for domains) */}
				{activeTab === "domains" && (
					<span className="quick-filter filter-locked">
						Distribution channel: Site
					</span>
				)}

				{/* Show "No filters applied" if no filters are active */}
				{!filters.mediaSubtype?.length &&
					!filters.deviceType?.length &&
					!filters.videoPlacement?.length &&
					!filters.selectedCountries?.length &&
					!filters.contentStyle?.length &&
					!filters.safetyTier?.length &&
					!filters.publishers?.length &&
					!filters.selectedCategories?.length &&
					!filters.customFilters?.length &&
					filters.isDirect === undefined &&
					!filters.relevance &&
					!filters.minViewability &&
					!filters.minCTR &&
					!filters.minReach && (
						<span className="quick-filter no-filters">No filters applied</span>
					)}
			</div>

			{/* Search Stage Indicator */}
			{searchStage > 0 && (
				<div className="search-stage-indicator">
					<span className="tag tag-orange">Stage {searchStage}</span>
					<span className="search-stage-text">
						{searchStage === 1 ? "Searching..." : "Search completed"}
					</span>
					{searchStage > 1 && onClearRefinement && (
						<button
							onClick={onClearRefinement}
							className="clear-refinement-btn"
						>
							<XIcon />
						</button>
					)}
				</div>
			)}

			<style jsx>{`
        .search-interface-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
}

.search-terms-display {
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  min-height: 36px;
  max-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  gap: 12px;
}

.search-terms-content {
  flex: 1;
  overflow: hidden;
}

.current-search-term {
  font-size: 13px;
  color: #374151;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.search-placeholder {
  color: #9ca3af;
  font-style: italic;
}

.search-term {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  margin: 2px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.term-include {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.term-exclude {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.term-remove-btn {
  margin-left: 4px;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-weight: bold;
  padding: 0 2px;
  border-radius: 2px;
  transition: background-color 0.2s;
}

.term-remove-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.search-input-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  padding: 0 8px 0 8px;
}

.search-input-wrapper :global(svg) {
  position: absolute;
  padding-left: 12px;
  width: 16px;
  height: 16px;
  color: #9ca3af;
  z-index: 1;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 16px 12px 4px;
  font-size: 14px;
  background: transparent;
}

.search-input:focus {
  outline: none;
}

.search-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.term-type-buttons {
  display: flex;
  gap: 4px;
  margin-right: 8px;
}

.term-type-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.include-btn {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.include-btn:hover:not(:disabled) {
  background: #059669;
  border-color: #059669;
}

.exclude-btn {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.exclude-btn:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
}

.term-type-btn:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  border-color: #d1d5db;
  cursor: not-allowed;
}

.tier-input-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 140px;
  max-width: 200px;
  position: relative;
  flex-shrink: 0;
}

.search-button-container {
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
}

.search-button {
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  min-width: 100px;
}

.search-button:hover:not(:disabled) {
  background: #2563eb;
}

.search-button:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-clear-all-inline {
  padding: 4px 12px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-clear-all-inline:hover {
  background: #dc2626;
}

.performance-filter-section {
  margin-bottom: 20px;
}

.performance-filter-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.performance-filter-container,
.target-container,
.sort-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 120px;
}

.performance-filter-container {
  flex: 1;
}

.target-container {
  flex: 1.2;
}

.sort-container {
  flex: 1;
}

.performance-label-container {
  display: flex;
  align-items: center;
  height: 20px;
}

.performance-filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  white-space: nowrap;
}

.performance-filter-select,
.target-type-select,
.sort-select {
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 11px;
  background: white;
  color: #374151;
  outline: none;
  cursor: pointer;
}

.performance-filter-select:focus,
.target-type-select:focus,
.sort-select:focus {
  border-color: #0076BD;
  box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
}

.target-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
}

.target-type-select {
  flex: 1;
  min-width: 100px;
}

.target-value-input-inline {
  width: 70px;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 11px;
  background: white;
  color: #374151;
  outline: none;
  text-align: center;
  flex-shrink: 0;
}

.target-value-input-inline:focus {
  border-color: #0076BD;
  box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
}

.sort-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.sort-select {
  flex: 1;
  min-width: 100px;
}

.sort-order-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: all 0.2s ease;
  user-select: none;
  flex-shrink: 0;
}

.sort-order-arrow:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
  color: #111827;
}

.sort-order-arrow:active {
  background: #d1d5db;
  transform: scale(0.95);
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.search-suggestion {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-suggestion:hover {
  background: #f3f4f6;
}

.search-stage-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
}

.tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.tag-orange {
  background: #fed7aa;
  color: #9a3412;
}

.search-stage-text {
  flex: 1;
  font-size: 14px;
  color: #92400e;
}

.clear-refinement-btn {
  background: none;
  border: none;
  color: #92400e;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.clear-refinement-btn:hover {
  background: rgba(146, 64, 14, 0.1);
}

.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  align-items: center;
}

.quick-filter {
  padding: 4px 8px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 4px;
  font-size: 11px;
  border: 1px solid #e5e7eb;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-locked {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #f59e0b;
  font-weight: 500;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-locked::after {
  content: "🔒";
  margin-left: 4px;
}

.no-filters {
  background: #e5e7eb;
  color: #6b7280;
  border: 1px solid #d1d5db;
  font-style: italic;
}

.search-interface-container * {
  box-sizing: border-box;
}

@media (max-width: 1024px) {
  .search-input-row {
    gap: 8px;
  }
  
  .tier-input-container {
    min-width: 120px;
  }
}

@media (max-width: 768px) {
  .search-interface-container {
    padding: 16px;
  }

  .search-input-row {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .search-input-wrapper {
    width: 100%;
    min-width: auto;
  }

  .tier-input-container,
  .search-button-container {
    width: 100%;
    align-items: stretch;
    min-width: auto;
    max-width: none;
  }

  .search-button {
    width: 100%;
    justify-content: center;
  }

  .performance-filter-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .performance-filter-container,
  .target-container,
  .sort-container {
    width: 100%;
    align-items: flex-start;
    flex: none;
    min-width: auto;
  }

  .performance-filter-select,
  .target-type-select,
  .sort-select {
    width: 100%;
  }

  .target-input-group,
  .sort-controls {
    width: 100%;
  }

  .target-input-group {
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
  }
  
  .target-type-select {
    flex: 1;
    min-width: auto;
  }
  
  .target-value-input-inline {
    width: 80px;
    flex-shrink: 0;
  }

  .sort-input-group {
    width: 100%;
    align-items: flex-start;
  }

  .term-type-buttons {
    margin-right: 4px;
  }

  .term-type-btn {
    padding: 4px 8px;
    font-size: 11px;
  }

  .search-terms-display {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    min-height: auto;
    max-height: none;
  }

  .btn-clear-all-inline {
    align-self: flex-end;
    padding: 6px 12px;
  }

  .quick-filters {
    gap: 4px;
  }

  .quick-filter {
    font-size: 10px;
    padding: 3px 6px;
  }
}

@media (max-width: 480px) {
  .search-interface-container {
    padding: 12px;
  }
  
  .search-input-row {
    gap: 8px;
  }

  .term-type-buttons {
    flex-wrap: wrap;
    gap: 2px;
  }

  .term-type-btn {
    padding: 4px 8px;
    font-size: 11px;
    flex: 1;
    min-width: 60px;
  }

  .target-input-group {
    flex-direction: column;
    gap: 6px;
  }
  
  .target-type-select,
  .target-value-input-inline {
    width: 100%;
  }
}
      `}</style>
		</div>
	);
};
